import { log } from '@hungryroot/o11y';
import {
  type FoodPreferencesIntroEntrySource,
  getFoodPreferencesIntroEntryRoute,
} from '../defs/foodPreferencesIntroEntry';
import { type QuizStorageNamespace } from '../defs/localStorage';
import {
  prepareExplicitSelectionsEntry,
  resolveExplicitSelectionsCompletion,
} from './explicitSelectionsCompletion';

interface Params {
  customerId: number;
  /** Which funnel's intro to send them to. */
  funnel: QuizStorageNamespace;
  source: FoodPreferencesIntroEntrySource;
  /** A checked-out customer is never sent back through the deck. */
  didCheckout?: boolean;
}

/**
 * The one explicit-selections gate every guest redirect shares: the intro
 * route to send a guest to when they have not been through the deck, or
 * `null` when they have (or that could not be determined — a failed read
 * must never send a guest who may already have finished the deck back
 * through it).
 *
 * The plan reset that keeps the funnel islands' `reset_survey` from wiping
 * the guest's answers on landing runs here, before the route is returned,
 * so every caller gets it without having to remember it.
 */
export async function resolveExplicitSelectionsIntroRoute({
  customerId,
  funnel,
  source,
  didCheckout = false,
}: Params): Promise<string | null> {
  const completion = didCheckout
    ? 'complete'
    : await resolveExplicitSelectionsCompletion();
  if (completion !== 'incomplete') {
    log('Resolved explicit selections entry', {
      key: 'resolveExplicitSelectionsIntroRoute.decision',
      customerId,
      funnel,
      source,
      completion,
      destination: 'continue',
    });
    return null;
  }

  await prepareExplicitSelectionsEntry({ customerId });
  const route = getFoodPreferencesIntroEntryRoute({ funnel, source });
  log('Resolved explicit selections entry', {
    key: 'resolveExplicitSelectionsIntroRoute.decision',
    customerId,
    funnel,
    source,
    completion,
    destination: 'intro',
  });
  return route;
}
